import React, { FunctionComponent } from 'react';
import {
  CardHeader,
  Divider,
  FileCopyOutlinedIcon,
  UnfoldMoreOutlinedIcon,
  UnfoldLessOutlinedIcon,
  Grid,
  IconButton,
  makeStyles,
} from '@rbx/ui';
import settings from '@modules/config/settings';
import { useTranslation } from '@rbx/intl';
import RunnableCodeButton from '../RunnableCodeBlockButton';

const useHeaderStyles = makeStyles()((theme) => ({
  header: {
    padding: 10,
    minHeight: 44,
    backgroundColor: theme.palette.media.toolbar,
  },
  headerTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...theme.typography.body1,
  },
  headerContent: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  headerAction: {
    margin: '-2px 0px',
  },
  iconButton: {
    marginLeft: '4px',
  },
  divider: {
    margin: 5,
  },
}));

export interface CodeBlockHeaderProps {
  title?: string;
  assetId?: number;
  enableCopy?: boolean;
  enableExpand?: boolean;
  isContentExpanded?: boolean;
  exceedsMaxHeight?: boolean;
  maxContentHeight?: number;
  onCopy?: () => void;
  onExpand?: () => void;
}

const CodeBlockHeader: FunctionComponent<CodeBlockHeaderProps> = ({
  title,
  assetId,
  enableCopy,
  enableExpand,
  isContentExpanded,
  exceedsMaxHeight,
  maxContentHeight,
  onCopy,
  onExpand,
}) => {
  const { classes } = useHeaderStyles();
  const { translate } = useTranslation();
  return (
    <React.Fragment>
      <CardHeader
        classes={{
          root: classes.header,
          title: classes.headerTitle,
          content: classes.headerContent,
          action: classes.headerAction,
        }}
        title={title}
        action={
          <Grid container direction='row'>
            {settings.enableRunCodeInStudio && assetId && (
              <RunnableCodeButton title={title} assetId={assetId} />
            )}
            {enableCopy && (
              <IconButton
                classes={{ root: classes.iconButton }}
                aria-label={translate('Label.CopyCodeToClipboard') || 'Copy code to clipboard'}
                color='default'
                size='small'
                onClick={onCopy}>
                <FileCopyOutlinedIcon fontSize='small' />
              </IconButton>
            )}
            {settings.enableRunCodeInStudio && assetId && (
              <Divider classes={{ root: classes.divider }} orientation='vertical' flexItem />
            )}
            {enableExpand && exceedsMaxHeight && (
              <IconButton
                classes={{ root: classes.iconButton }}
                aria-label={
                  isContentExpanded
                    ? translate('Label.CollapseCodeBlock') || 'Collapse code block'
                    : translate('Label.ExpandCodeBlock') || 'Expand code block'
                }
                color='default'
                size='small'
                onClick={onExpand}>
                {isContentExpanded && typeof maxContentHeight !== 'undefined' ? (
                  <UnfoldLessOutlinedIcon fontSize='small' />
                ) : (
                  <UnfoldMoreOutlinedIcon fontSize='small' />
                )}
              </IconButton>
            )}
          </Grid>
        }
      />
      <Divider />
    </React.Fragment>
  );
};

export default CodeBlockHeader;
